'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { PublicNewsFeed, PublicNewsItem } from '@/lib/blog-news';

type Snapshot = { items: PublicNewsItem[] | null; now: number | null; checkedAt: number | null };
const EMPTY: Snapshot = { items: null, now: null, checkedAt: null };
let snapshot = EMPTY;
let anchor: { server: number; monotonic: number } | null = null;
let request: AbortController | null = null;
const listeners = new Set<() => void>();
let stop: (() => void) | null = null;
const isHidden = () => document.visibilityState === 'hidden';

function publish(next: Snapshot) {
  snapshot = next;
  listeners.forEach(listener => listener());
}

function invalidate() {
  anchor = null;
  request?.abort();
  request = null;
  publish({ ...snapshot, now: null });
}

async function refresh() {
  if (request || isHidden()) return;
  const controller = new AbortController();
  request = controller;
  const timeout = window.setTimeout(() => controller.abort(), 10_000);
  try {
    const response = await fetch('/api/blog/actualidad', { cache: 'no-store', signal: controller.signal });
    if (!response.ok) throw new Error('Actualidad no disponible');
    const feed: PublicNewsFeed = await response.json();
    if (!Number.isFinite(feed.serverNow) || !Array.isArray(feed.items)) throw new Error('Respuesta inválida');
    if (request !== controller || isHidden()) return;
    anchor = { server: feed.serverNow, monotonic: performance.now() };
    publish({ items: feed.items, now: feed.serverNow, checkedAt: feed.serverNow });
  } catch {
    if (request === controller) {
      anchor = null;
      publish({ ...snapshot, now: null });
    }
  } finally {
    window.clearTimeout(timeout);
    if (request === controller) request = null;
  }
}

function start() {
  // Una respuesta prefetched o restaurada no conserva autoridad temporal.
  invalidate();
  void refresh();
  const tick = window.setInterval(() => {
    if (!anchor) return;
    const elapsed = performance.now() - anchor.monotonic;
    if (elapsed < 0 || elapsed >= 60_000) {
      invalidate();
      void refresh();
    } else {
      publish({ ...snapshot, now: anchor.server + elapsed });
    }
  }, 1_000);
  const poll = window.setInterval(() => { void refresh(); }, 30_000);
  const resume = () => { invalidate(); void refresh(); };
  const visibility = () => {
    invalidate();
    if (document.visibilityState !== 'hidden') void refresh();
  };
  window.addEventListener('pagehide', invalidate);
  window.addEventListener('pageshow', resume);
  document.addEventListener('visibilitychange', visibility);
  return () => {
    window.clearInterval(tick);
    window.clearInterval(poll);
    window.removeEventListener('pagehide', invalidate);
    window.removeEventListener('pageshow', resume);
    document.removeEventListener('visibilitychange', visibility);
    invalidate();
    snapshot = EMPTY;
  };
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) stop = start();
  return () => {
    listeners.delete(listener);
    if (!listeners.size) { stop?.(); stop = null; }
  };
}
const getSnapshot = () => snapshot;

/** Un solo refresco por página aunque haya varios módulos; SSR conserva su corte. */
export function useNewsFeed(initial: PublicNewsFeed) {
  const server = useMemo<Snapshot>(() => ({ items: initial.items, now: initial.serverNow, checkedAt: initial.serverNow }), [initial]);
  const getServerSnapshot = useCallback(() => server, [server]);
  const current = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { ...current, items: current.items ?? initial.items };
}
